import styled from 'styled-components';
import { convertTimeToMMSS } from '../../utility/timeFormat';
import { memo, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import MuxPlayerClip from '../common/MuxPlayerClip';
import ImpactScoreBadge from './ImpactScoreBadge';

const AIClipMemoized = memo(
  ({
    fragment,
    index,
    isSelected,
    onSelect: onSelectByIndex,
    isPlaying,
    onPlay: onPlayByIndex,
  }: any) => {
    const onSelect = useCallback(
      () => onSelectByIndex(index),
      [onSelectByIndex, index],
    );

    const onPlay = useCallback(() => {
      onPlayByIndex(index);
    }, [onPlayByIndex, index]);

    return (
      <AIClip
        fragment={fragment}
        isSelected={isSelected}
        onSelect={onSelect}
        isPlaying={isPlaying}
        onPlay={onPlay}
        index={index}
      />
    );
  },
);

const AIClip = memo(
  ({
    fragment,
    isSelected,
    onSelect,
    isPlaying,
    onPlay,
    index,
  }: {
    fragment: any;
    isSelected: boolean;
    onSelect: () => void;
    isPlaying: boolean;
    onPlay: () => void;
    index: number;
  }) => {
    const duration = Math.round(fragment.duration);
    const durationFormatted = duration ? convertTimeToMMSS(duration) : '';

    const title = fragment.title;
    const description = fragment.text;
    const startTimeFormatted = convertTimeToMMSS(
      Math.round(fragment.startTime),
    );
    const endTimeFormatted = convertTimeToMMSS(
      Math.round(fragment.startTime + fragment.duration),
    );

    return (
      <Card onClick={onSelect}>
        <SelectButton active={isSelected}>{isSelected ? '✓' : ''}</SelectButton>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <DurationBadge>{durationFormatted}</DurationBadge>
          <ImpactScoreBadge
            impactScore={fragment.impactScore}
            id={`impact-score-${index}`}
          />
        </div>
        <MuxPlayerClip clip={fragment} isPlaying={isPlaying} onPlay={onPlay} />
        {title && (
          <>
            <Title data-tooltip-id="title-tooltip" data-tooltip-content={title}>
              {title}
            </Title>
            <Tooltip
              id="title-tooltip"
              style={{
                width: '260px',
                padding: '5px',
                fontSize: '10px',
                lineHeight: '120%',
                zIndex: '10',
              }}
            />
          </>
        )}
        {/* Display speaker badge if clip has speaker information */}
        {fragment.speakerName && (
          <SpeakerBadge>{fragment.speakerName}</SpeakerBadge>
        )}
        <Description
          data-tooltip-id="description-tooltip"
          data-tooltip-content={description}
        >
          {description}
        </Description>
        <Tooltip
          id="description-tooltip"
          style={{
            width: '260px',
            padding: '5px',
            fontSize: '10px',
            lineHeight: '120%',
            zIndex: '10',
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <TimeRanges>
            {startTimeFormatted} - {endTimeFormatted}{' '}
          </TimeRanges>
          <Theme>{fragment.theme}</Theme>
        </div>
      </Card>
    );
  },
);

export default AIClipMemoized;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 286px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #484848;
  box-sizing: border-box;
  color: #f3e9d7;
  height: fit-content;
  cursor: pointer;

  &:hover {
    box-shadow: 0px 0px 4px #484848c0;
  }
`;

const SelectButton = styled.div.attrs((props: { active: boolean }) => props)`
  position: absolute;
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 20px;
  right: 16px;
  top: 16px;
  margin: auto;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  padding: 0px 0px 0 0;
  box-sizing: border-box;
  color: #484848;
  border: 1px solid #484848;
  ${(props) =>
    props.active &&
    `
      color: #45d483;
      border: 1px solid #45d483;
    `}
`;

const DurationBadge = styled.div`
  padding: 2px 5px;
  background-color: #f178b6;
  color: black;
  width: fit-content;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
`;

const Title = styled.div`
  color: #f3e9d7;
  font-size: 16px;
  font-weight: 700;
  line-height: 21px;
  height: 21px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Description = styled.div`
  height: 40px;
  color: #f3e9d7;
  font-size: 10px;
  font-weight: 400;
  line-height: 140%; /* 14px */
  text-overflow: ellipsis;
  overflow: hidden;
  margin: 6px 0 16px 0;
`;

const Theme = styled.div`
  width: fit-content;
  padding: 8px;
  color: #484848;
  font-size: 10px;
  font-weight: 500;
  line-height: normal;
  border-radius: 4px;
  border: 1px solid #484848;
  text-transform: capitalize;
`;

const TimeRanges = styled.div`
  color: #484848;
  font-family: Inter;
  font-size: 10px;
  font-weight: 500;
  line-height: normal;
`;

const SpeakerBadge = styled.div`
  display: inline-flex;
  padding: 4px 0px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 500;
  line-height: normal;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
